import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Viewer360 } from "@/components/Viewer360";
import {
  open360Camera,
  listBackCameras,
  applyDefaultZoom,
  applyWidestZoom,
  defaultWidestBackDeviceId,
  switchableBackCameras,
  isLandscapeViewport,
} from "@/lib/wideCamera";
import {
  Loader2, Video, StopCircle, Circle,
  Sparkles, RotateCw, Camera, SwitchCamera, Check, AlertTriangle, Smartphone,
} from "lucide-react";
import { CameraCoachBar } from "@/components/CameraCoachBar";
import { getCameraCoachUi, runLivePhotoCheck } from "@/lib/cameraLiveCheck";
import { canUseInPageCamera, inPageCameraBlockReason } from "@/lib/cameraEnv";
import {
  exitImmersiveFullscreen,
  isStandalonePwa,
  readVisualViewportBox,
  requestImmersiveFullscreen,
} from "@/lib/immersiveFullscreen";
import { WALKAROUND_FRAME_COUNT } from "@/lib/viewer360Frames";

/** Gyors körbejárás ~25 mp — addig nem állítható le. */
const TARGET_RECORD_SEC = 25;
const MIN_RECORD_SEC = TARGET_RECORD_SEC;
/** Lassabb séta + pár mp ráhagyás; felette auto stop (ne legyen perces felvétel). */
const MAX_RECORD_SEC = 45;
const API_LONG_TIMEOUT_MS = 300000;

async function uploadVideo(file) {
  const fd = new FormData();
  fd.append("files", file);
  const r = await api.post("/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: API_LONG_TIMEOUT_MS,
  });
  return r.data.urls?.[0] || r.data.url;
}

function apiErrorDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || String(x)).join(" ");
  return fallback;
}

/** Safari gyakran üres blob.type — magic byte alapján mp4/webm. */
async function videoFileFromBlob(blob, mimeHint) {
  const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  const isMp4 = head.length >= 8 && head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70;
  const isWebm = head.length >= 4 && head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3;
  let ext = "webm";
  let type = mimeHint || blob.type || "video/webm";
  if (isMp4 || type.includes("mp4") || type.includes("quicktime")) {
    ext = "mp4";
    type = type.includes("video/") ? type : "video/mp4";
  } else if (isWebm || type.includes("webm")) {
    ext = "webm";
    type = type.includes("video/") ? type : "video/webm";
  } else if (type.includes("mp4")) {
    ext = "mp4";
  }
  return new File([blob], `walkaround_${Date.now()}.${ext}`, { type });
}

export function Video360Recorder({ onProcessed, disabled }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("video/webm");
  const coachTimerRef = useRef(null);
  const coachBusyRef = useRef(false);
  const selectedDeviceRef = useRef(null);
  /** wide = max látómező (alap); main = Lencse gombbal választott 1× */
  const [lensMode, setLensMode] = useState("wide");
  const finalizeRecordingRef = useRef(null);
  const fullscreenRootRef = useRef(null);
  const recordStartedAtRef = useRef(0);
  const stopRecordingRef = useRef(null);
  const recordHintShownRef = useRef(false);

  const [mode, setMode] = useState("idle"); // idle | preview | recording | processing
  const [recording, setRecording] = useState(false);
  const [recordElapsed, setRecordElapsed] = useState(0);
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachOffline, setCoachOffline] = useState(false);
  const [progress, setProgress] = useState("");
  const [cameras, setCameras] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isLandscape, setIsLandscape] = useState(isLandscapeViewport);
  const [viewportBox, setViewportBox] = useState(() => readVisualViewportBox());

  const showPreview = mode === "preview" || mode === "recording" || recording;
  const fullscreenActive = showPreview || mode === "processing";
  const cameraBlockReason = inPageCameraBlockReason();
  const cameraOk = canUseInPageCamera();
  const switchable = switchableBackCameras(cameras);
  const canSwitchLens = switchable.length > 1;

  const coachUi = getCameraCoachUi(coach, {
    loading: coachLoading,
    offline: coachOffline,
    advisoryOnly: true,
  });
  if (coachUi) coachUi.testId = "video-360-coach";

  const syncViewport = useCallback(() => {
    setIsLandscape(isLandscapeViewport());
    setViewportBox(readVisualViewportBox());
  }, []);

  useEffect(() => {
    syncViewport();
    window.addEventListener("resize", syncViewport);
    window.addEventListener("orientationchange", syncViewport);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", syncViewport);
    vv?.addEventListener("scroll", syncViewport);
    return () => {
      window.removeEventListener("resize", syncViewport);
      window.removeEventListener("orientationchange", syncViewport);
      vv?.removeEventListener("resize", syncViewport);
      vv?.removeEventListener("scroll", syncViewport);
    };
  }, [syncViewport]);

  useEffect(() => {
    if (!fullscreenActive) {
      void exitImmersiveFullscreen();
      return undefined;
    }
    const id = requestAnimationFrame(() => {
      void requestImmersiveFullscreen(fullscreenRootRef.current);
    });
    return () => {
      cancelAnimationFrame(id);
      void exitImmersiveFullscreen();
    };
  }, [fullscreenActive]);

  useEffect(() => {
    if (!fullscreenActive) {
      document.body.style.overflow = "";
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [fullscreenActive]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => () => {
    stopStream();
    if (coachTimerRef.current) clearInterval(coachTimerRef.current);
    document.body.style.overflow = "";
  }, [stopStream]);

  const bindStreamToVideo = useCallback(async () => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    if (video.srcObject !== stream) {
      video.srcObject = stream;
    }
    try {
      await video.play();
    } catch (_) {
      /* iOS néha async play()-t kér újra mount után */
    }
  }, []);

  const storeStream = useCallback((stream) => {
    streamRef.current = stream;
    const devId = stream._deviceId || stream.getVideoTracks()[0]?.getSettings?.()?.deviceId;
    if (devId) {
      selectedDeviceRef.current = devId;
      setActiveDeviceId(devId);
    }
  }, []);

  useEffect(() => {
    if (!fullscreenActive) return;
    const id = requestAnimationFrame(() => {
      void bindStreamToVideo();
    });
    return () => cancelAnimationFrame(id);
  }, [fullscreenActive, mode, previewLoading, bindStreamToVideo]);

  const startPreview = useCallback(async (deviceId, previewOpts = {}) => {
    if (disabled) return;
    const block = inPageCameraBlockReason();
    if (block) {
      toast.error(block, { duration: 8000 });
      return;
    }
    setPreviewLoading(true);
    setMode("preview");
    stopStream();
    try {
      const list = await listBackCameras();
      setCameras(list);
      let forceMainLens = previewOpts.forceMainLens;
      if (deviceId == null && previewOpts.forceMainLens === undefined) {
        selectedDeviceRef.current = defaultWidestBackDeviceId(list);
        setLensMode("wide");
        forceMainLens = false;
      } else if (forceMainLens === undefined) {
        forceMainLens = lensMode === "main";
      }
      const pickId = deviceId ?? selectedDeviceRef.current ?? defaultWidestBackDeviceId(list);
      const stream = await open360Camera(pickId, list, { forceMainLens });
      storeStream(stream);
      await bindStreamToVideo();
      requestAnimationFrame(() => {
        void requestImmersiveFullscreen(fullscreenRootRef.current || document.documentElement);
      });
    } catch (_) {
      toast.error("A kamera nem elérhető. Engedélyezd a kamerát, majd próbáld újra.");
      setMode("idle");
    } finally {
      setPreviewLoading(false);
    }
  }, [bindStreamToVideo, disabled, lensMode, stopStream, storeStream]);

  useEffect(() => {
    if (mode !== "preview" || recording) return;
    let t;
    const onOri = () => {
      clearTimeout(t);
      t = window.setTimeout(() => {
        const id = selectedDeviceRef.current;
        if (id && mode === "preview" && !recording) void startPreview(id);
      }, 450);
    };
    window.addEventListener("orientationchange", onOri);
    return () => {
      clearTimeout(t);
      window.removeEventListener("orientationchange", onOri);
    };
  }, [mode, recording, startPreview]);

  const switchLens = async () => {
    if (recording || mode === "processing" || switchable.length < 2) return;
    const idx = switchable.findIndex((c) => c.deviceId === activeDeviceId);
    const next = switchable[(idx + 1) % switchable.length];
    if (!next) return;
    const nextMode = next.isUltra ? "wide" : "main";
    setLensMode(nextMode);
    await startPreview(next.deviceId, { forceMainLens: nextMode === "main" });
  };

  const closeCamera = () => {
    if (recording) return;
    stopStream();
    selectedDeviceRef.current = null;
    setLensMode("wide");
    setMode("idle");
    setCoach(null);
  };

  const runCoach = useCallback(async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || coachBusyRef.current) return;
    coachBusyRef.current = true;
    setCoachLoading(true);
    try {
      const data = await runLivePhotoCheck(video);
      setCoach(data);
      setCoachOffline(false);
    } catch (_) {
      setCoachOffline(true);
    } finally {
      coachBusyRef.current = false;
      setCoachLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode !== "preview" && !recording) {
      setCoach(null);
      if (coachTimerRef.current) clearInterval(coachTimerRef.current);
      return;
    }
    runCoach();
    coachTimerRef.current = setInterval(runCoach, 2800);
    return () => {
      if (coachTimerRef.current) clearInterval(coachTimerRef.current);
    };
  }, [mode, recording, runCoach]);

  const beginRecording = async () => {
    if (disabled) return;
    if (!isLandscape) {
      toast.error("Forgasd vízszintesre a telefont a felvételhez.", { duration: 5000 });
      return;
    }
    let stream = streamRef.current;
    if (!stream) {
      await startPreview();
      stream = streamRef.current;
    }
    if (!stream) return;

    try {
      if (lensMode === "main") await applyDefaultZoom(stream);
      else await applyWidestZoom(stream);
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("video/mp4")
        ? "video/mp4"
        : (MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm");
      mimeRef.current = mime;
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
      recorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => { void finalizeRecordingRef.current?.(); };
      rec.start(250);
      recordStartedAtRef.current = Date.now();
      setRecording(true);
      setMode("recording");
      void requestImmersiveFullscreen(fullscreenRootRef.current || document.documentElement);
      toast.info(`Sétálj végig egy teljes körön az autó körül (~${TARGET_RECORD_SEC} mp), utána Leállítás`, { duration: 7000 });
    } catch (_) {
      toast.error("Felvétel indítása sikertelen");
    }
  };

  const processVideoUrl = async (videoUrl) => {
    setMode("processing");
    setProgress("Stabilizálás és kockák kivágása…");
    try {
      const r = await api.post("/360/process-video", { video_url: videoUrl, frame_count: WALKAROUND_FRAME_COUNT }, {
        timeout: API_LONG_TIMEOUT_MS,
      });
      onProcessed?.(r.data);
      const qa = r.data.qa;
      if (qa?.score === "ok") toast.success(`360° kész — ${r.data.frame_count} kocka`);
      else if (qa?.score === "warning") toast.warning(qa.advice || "360° kész, de lehetne jobb is");
      else toast.error(qa?.advice || "360° kész, de érdemes újra felvenni");
    } catch (e) {
      toast.error(apiErrorDetail(e, "Videó feldolgozás sikertelen"), { duration: 9000 });
    } finally {
      setMode("idle");
      setProgress("");
      setCoach(null);
    }
  };

  const handleRecordedBlob = async (blob) => {
    if (!blob?.size) return;
    setMode("processing");
    setProgress("Videó feltöltése…");
    try {
      const file = await videoFileFromBlob(blob, mimeRef.current);
      const url = await uploadVideo(file);
      await processVideoUrl(url);
    } catch (e) {
      toast.error(apiErrorDetail(e, "Feltöltés sikertelen"), { duration: 9000 });
      setMode("idle");
      setProgress("");
    }
  };

  finalizeRecordingRef.current = async () => {
    const blob = new Blob(chunksRef.current, { type: mimeRef.current });
    chunksRef.current = [];
    recorderRef.current = null;
    stopStream();
    if (!blob.size) {
      toast.error("A felvétel üres — próbáld újra");
      setMode("idle");
      setProgress("");
      return;
    }
    if (blob.size < 800) {
      toast.error(`A videó túl kicsi — vedd fel újra, legalább ${MIN_RECORD_SEC} másodpercig.`, { duration: 8000 });
      setMode("idle");
      setProgress("");
      return;
    }
    await handleRecordedBlob(blob);
  };

  const stopRecording = () => {
    const elapsedSec = recordStartedAtRef.current
      ? (Date.now() - recordStartedAtRef.current) / 1000
      : 0;
    if (elapsedSec > 0 && elapsedSec < MIN_RECORD_SEC) {
      toast.error(
        `Tartsd nyomva még ${(MIN_RECORD_SEC - elapsedSec).toFixed(1)} mp-ig — minimum ${MIN_RECORD_SEC} mp kell a feldolgozáshoz.`,
        { duration: 7000 },
      );
      return;
    }
    if (coachTimerRef.current) clearInterval(coachTimerRef.current);
    setRecording(false);
    setCoach(null);
    const rec = recorderRef.current;
    if (!rec || rec.state === "inactive") {
      void finalizeRecordingRef.current?.();
      return;
    }
    rec.onstop = () => { void finalizeRecordingRef.current?.(); };
    try {
      if (rec.state === "recording") rec.requestData();
      rec.stop();
    } catch (_) {
      void finalizeRecordingRef.current?.();
    }
  };
  stopRecordingRef.current = stopRecording;

  useEffect(() => {
    if (!recording) {
      recordHintShownRef.current = false;
      setRecordElapsed(0);
      return undefined;
    }
    const tick = window.setInterval(() => {
      const elapsed = recordStartedAtRef.current
        ? (Date.now() - recordStartedAtRef.current) / 1000
        : 0;
      setRecordElapsed(elapsed);
      if (elapsed >= TARGET_RECORD_SEC && !recordHintShownRef.current) {
        recordHintShownRef.current = true;
        toast.info("Ha megvolt a teljes kör, nyomd meg a Leállítást.", { duration: 6000 });
      }
      if (elapsed >= MAX_RECORD_SEC) {
        toast.warning("Maximális felvételi idő — leállítjuk és feldolgozzuk.", { duration: 5000 });
        stopRecordingRef.current?.();
      }
    }, 250);
    return () => window.clearInterval(tick);
  }, [recording]);

  const orientationCoach = !isLandscape
    ? {
        tone: "warn",
        car: "Forgasd el vízszintesen a telefont",
        light: "",
        detail: "A 360° walkaround videót fekvő (landscape) módban vedd fel — így az autó teljes szélességben látszik, levágás nélkül.",
        badge: "AI",
        testId: "video-360-orientation-coach",
      }
    : null;

  const activeCoach = orientationCoach || coachUi;

  const lensHint =
    lensMode === "main"
      ? "Normál lencse (1×) — kattints szélesre"
      : "Széles lencse — kattints normálra";

  const stageMaxW = viewportBox.width;
  const stageMaxH = viewportBox.height;

  const fullscreenUi = fullscreenActive
    ? createPortal(
        <div
          ref={fullscreenRootRef}
          data-testid="video-360-fullscreen"
          className="fixed z-[300] bg-black touch-none overflow-hidden"
          style={{
            top: viewportBox.top,
            left: viewportBox.left,
            width: viewportBox.width,
            height: viewportBox.height,
            paddingTop: "env(safe-area-inset-top, 0px)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            paddingLeft: "env(safe-area-inset-left, 0px)",
            paddingRight: "env(safe-area-inset-right, 0px)",
          }}
        >
          <div
            className="absolute inset-0 grid place-items-center bg-black"
            style={{
              width: stageMaxW,
              height: stageMaxH,
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              onLoadedMetadata={() => {
                syncViewport();
                void bindStreamToVideo();
              }}
              className="block max-w-full max-h-full w-auto h-auto"
              style={{
                maxWidth: stageMaxW,
                maxHeight: stageMaxH,
                objectFit: "contain",
              }}
            />
          </div>

          {!showPreview && mode === "processing" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 z-20">
              <Loader2 className="w-10 h-10 text-electric animate-spin" />
              <p className="mt-4 text-sm text-slate-200 px-6 text-center">{progress || "Feldolgozás…"}</p>
            </div>
          )}

          {previewLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-20">
              <Loader2 className="w-9 h-9 text-electric animate-spin" />
            </div>
          )}

          {showPreview && (
            <>
              <div
                className="pointer-events-none absolute inset-x-0 top-0 z-20 px-3 pt-2 max-w-lg mx-auto w-full"
                style={{ paddingTop: "max(8px, env(safe-area-inset-top))" }}
              >
                {activeCoach && <CameraCoachBar ui={activeCoach} />}
              </div>

              {!isLandscape && (
                <div className="pointer-events-none absolute inset-x-0 bottom-32 flex justify-center px-6 z-20">
                  <div className="rounded-2xl border border-amber-400/40 bg-black/55 backdrop-blur-md p-4 text-center max-w-sm">
                    <Smartphone className="w-10 h-10 text-amber-300 mx-auto mb-2 rotate-90" strokeWidth={1.25} />
                    <p className="text-sm font-medium text-amber-100">Fekvő mód szükséges</p>
                    <p className="text-xs text-amber-100/80 mt-1 leading-relaxed">
                      Fordítsd el vízszintesen — a kamera preview így tölti ki a képernyőt.
                    </p>
                  </div>
                </div>
              )}

              {recording && (
                <div
                  className="absolute right-4 z-20 flex flex-col items-end gap-1"
                  style={{ top: "max(12px, env(safe-area-inset-top))" }}
                >
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600/90 text-white text-xs font-medium">
                    <Circle className="w-2.5 h-2.5 fill-white animate-pulse" />
                    REC {recordElapsed.toFixed(1)}s
                  </div>
                  <span className="text-[10px] text-white/75 px-1 text-right">
                    {recordElapsed < MIN_RECORD_SEC
                      ? `Min. ${MIN_RECORD_SEC} s (még ${Math.max(0, MIN_RECORD_SEC - recordElapsed).toFixed(0)} s)`
                      : `~${TARGET_RECORD_SEC} s kör — leállítható`}
                  </span>
                </div>
              )}

              <div
                className="absolute inset-x-0 bottom-0 z-30 px-4 pt-10 pb-4 flex flex-wrap gap-3 items-center justify-center bg-gradient-to-t from-black via-black/75 to-transparent"
                style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
              >
                {mode === "preview" && !recording && (
                  <>
                    <button
                      type="button"
                      data-testid="video-360-record-start"
                      disabled={disabled || !isLandscape}
                      onClick={beginRecording}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-500 hover:bg-red-400 disabled:opacity-40 text-white text-sm font-semibold"
                    >
                      <Video className="w-5 h-5" /> Felvétel indítása
                    </button>
                    <button
                      type="button"
                      disabled={previewLoading}
                      onClick={closeCamera}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 text-sm text-white"
                    >
                      Mégse
                    </button>
                    {canSwitchLens && (
                      <button
                        type="button"
                        disabled={previewLoading}
                        onClick={switchLens}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 text-sm text-white"
                        title={lensHint}
                      >
                        <SwitchCamera className="w-4 h-4" /> Lencse
                      </button>
                    )}
                  </>
                )}
                {recording && (
                  <button
                    type="button"
                    data-testid="video-360-record-stop"
                    disabled={recordElapsed < MIN_RECORD_SEC}
                    onClick={stopRecording}
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-red-500 hover:bg-red-400 disabled:opacity-45 disabled:pointer-events-none text-white text-base font-semibold"
                  >
                    <StopCircle className="w-5 h-5" />
                    {recordElapsed < MIN_RECORD_SEC ? `Leállítás (${MIN_RECORD_SEC} s)` : "Leállítás"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div data-testid="video-360-recorder" className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
      {fullscreenUi}

      {!fullscreenActive && (
        <div className="relative w-full aspect-video bg-black flex items-center justify-center">
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-gradient-to-b from-anthracite-900 to-black">
            <RotateCw className="w-10 h-10 text-electric mb-3" strokeWidth={1.25} />
            <p className="text-sm text-slate-200 max-w-md">
              Teljes képernyős kamera — <b>fekvő</b> tartásban vedd fel a walkaround videót.
              Egy teljes kör ~{TARGET_RECORD_SEC} mp alatt (gyors séta), ~1,5–2,5 m távolság. Az AI jelzi, ha nem látszik az autó.
            </p>
          </div>
        </div>
      )}

      {!cameraOk && cameraBlockReason && (
        <div
          data-testid="video-360-secure-context-hint"
          className="mx-4 mb-0 mt-3 rounded-xl border border-amber-400/35 bg-amber-500/10 p-3 text-xs text-amber-100 leading-relaxed"
        >
          {cameraBlockReason}
        </div>
      )}
      {cameraOk && !isStandalonePwa() && mode === "idle" && (
        <p
          data-testid="video-360-standalone-hint"
          className="mx-4 mt-3 text-[11px] text-slate-500 leading-relaxed text-center"
        >
          Kevesebb böngésző-sáv: telefonon <b>Megosztás → Kezdőképernyő</b>, onnan nyisd meg az AutoParkot — vagy a kamera megnyitásakor fogadd el a teljes képernyőt.
        </p>
      )}

      <div className="p-4 flex flex-wrap gap-3 items-center justify-center border-t border-white/5">
        {mode === "idle" && !recording && (
          <button
            type="button"
            data-testid="video-360-open-camera"
            disabled={disabled || previewLoading || !cameraOk}
            onClick={() => startPreview()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white text-sm font-medium"
          >
            <Camera className="w-4 h-4" /> Kamera megnyitása
          </button>
        )}
      </div>
    </div>
  );
}

export function Section360Video({ value, onChange, videoUrl, onVideoUrlChange }) {
  const frames = Array.isArray(value) ? value.filter((u) => typeof u === "string") : [];
  const [qa, setQa] = useState(null);
  const [meta, setMeta] = useState(null);

  const onProcessed = (data) => {
    onChange(data.frames || []);
    onVideoUrlChange?.(data.video_url || "");
    setQa(data.qa || null);
    setMeta({ stabilized: data.stabilized, duration: data.duration_sec, count: data.frame_count });
  };

  return (
    <div data-testid="listing-360-section" className="mt-8 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-electric" strokeWidth={1.75} />
          <h3 className="font-heading text-lg font-medium">360° körbeforgatás — videó</h3>
        </div>
        <div className="text-xs text-slate-400" data-testid="section-360-progress">
          <b className={frames.length >= 12 ? "text-emerald-400" : "text-electric"}>{frames.length}</b> kocka
        </div>
      </div>

      <p className="text-xs text-slate-500">
        Csak itt, az alkalmazáson belül vehetsz fel videót. Nyisd meg a teljes képernyős kamerát, forgasd el{" "}
        <b>vízszintesen</b> a telefont, és sétálj <b>egy teljes kört</b> az autó körül (~{TARGET_RECORD_SEC} mp, ~1,5–2,5 m).
        Az autó legyen végig teljes szélességben a képen — ne egy helyről pörgess. Ha nem fér ki, a{" "}
        <b>Lencse</b> gombbal válthatsz szélesebb nézetre. Utána stabilizálás és kockavágás.
      </p>
      {videoUrl && (
        <p className="text-[10px] text-slate-600 truncate" data-testid="section-360-source-video">
          Forrás videó rögzítve
        </p>
      )}

      <Video360Recorder onProcessed={onProcessed} disabled={false} />

      {qa && (
        <div
          data-testid="video-360-qa"
          className={`rounded-xl p-4 border text-sm flex items-start gap-2 ${
            qa.score === "ok"
              ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-100"
              : qa.score === "warning"
                ? "border-amber-400/40 bg-amber-500/10 text-amber-100"
                : "border-red-400/40 bg-red-500/10 text-red-100"
          }`}
        >
          {qa.score === "ok" ? <Check className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
          <div>
            <div className="font-medium capitalize">{qa.score === "ok" ? "Minőség rendben" : qa.score === "warning" ? "Elfogadható" : "Érdemes újra felvenni"}</div>
            {qa.advice && <div className="text-xs mt-1 opacity-90">{qa.advice}</div>}
            {meta && (
              <div className="text-[10px] mt-2 opacity-70">
                {meta.count} kocka · {meta.duration}s · {meta.stabilized ? "stabilizálva" : "nyers"}
              </div>
            )}
          </div>
        </div>
      )}

      {frames.length >= 2 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Előnézet — húzd a kurzort</div>
          <Viewer360 frames={frames} autoRotate={false} />
        </div>
      )}
    </div>
  );
}
